import React, { useState, useRef, useEffect } from 'react';
import Hls from 'hls.js';
import VideoControls from './VideoControls';

import { global } from '../api/global'

const VideoPlayer = ({ src,   subtitles = [], fileName }) => {
    const videoRef = useRef(null);
    const playerContainerRef = useRef(null);
    const hideControlsTimerRef = useRef(null);
    const hlsRef = useRef(null);

    // Core video state
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackError, setPlaybackError] = useState(null);

    // Feature state
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [areControlsVisible, setAreControlsVisible] = useState(true);
    const [activeSubtitleId, setActiveSubtitleId] = useState(null);
    const [activeQualityId, setActiveQualityId] = useState('auto');
    const [availableQualities, setAvailableQualities] = useState([]);


    let url = src;

    // --- HLS.js Integration ---
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;



        var defaultOptions = {};

        if (global.token) {

            url = url.replace('/rec/', '/api/stream/')

            defaultOptions = {
                debug: false, // Reduce console noise
                autoStartLoad: true,
                startPosition: -1,
                enableWorker: true,
                liveBackBufferLength: 4000,
                liveSyncDurationCount: 10,
                
                // Enhanced decryption settings
                enableSoftwareAES: true,
                enableContentSteering: false, // Disable content steering if causing issues
                
                xhrSetup: (xhr, url) => {
                    xhr.setRequestHeader('Authorization', `Bearer ${global.token}`);
                }
            };


        } else {

            defaultOptions = {

                autoStartLoad: true,
                startPosition: -1,
                enableWorker: true,

                liveBackBufferLength: 4000,
                liveSyncDurationCount: 10,

            };

        }




        const _initPlayer = () => {
            if (Hls.isSupported()) {
                const hls = new Hls(defaultOptions);
                hlsRef.current = hls;

                hls.attachMedia(video);

                hls.on(Hls.Events.MEDIA_ATTACHED, function () {
                    //   console.log("video and hls.js are now bound together !");
                    hls.loadSource(url);



                });

                hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                    const newQualities = data.levels.map((level, index) => ({
                        id: index,
                        label: `${level.height}p`,
                    }));
                    setAvailableQualities(newQualities);
                });


                hls.on(Hls.Events.ERROR, (event, data) => {
                    if (data.fatal) {
                        console.error('HLS.js fatal error:', data);
                        setPlaybackError('Ocorreu um erro ao carregar o vídeo.');
                    }
                });

            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Native HLS support on Safari
                video.src = url;
            }
        };

        _initPlayer();

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [url]);

    // --- Select default subtitle ---
    useEffect(() => {
        const video = videoRef.current;
        if (subtitles && subtitles.length > 0 && video) {
            const defaultSubtitle = subtitles.find(s => s.default) || subtitles[0];

            if (defaultSubtitle) {
                // A small delay can help ensure the browser has processed the <track> elements.
                const timer = setTimeout(() => {
                    if (videoRef.current) { // Ensure component is still mounted
                        handleSelectSubtitle(defaultSubtitle.id);
                    }
                }, 100); // Reduced delay as the new handleSelectSubtitle is more robust

                return () => clearTimeout(timer);
            }
        }
    }, [subtitles]);

    // --- Core Event Handlers ---
    const handlePlayPauseToggle = () => {
        if (videoRef.current.paused) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    const handleVolumeChange = (newVolume) => {
        videoRef.current.volume = newVolume;
        setVolume(newVolume);
        if (newVolume > 0 && isMuted) {
            setIsMuted(false);
            videoRef.current.muted = false;
        }
    };

    const handleToggleMute = () => {
        const newMutedState = !isMuted;
        videoRef.current.muted = newMutedState;
        setIsMuted(newMutedState);
        if (!newMutedState && volume === 0) {
            handleVolumeChange(0.5); // Unmute to a default volume
        }
    };

    const handleSeek = (time) => {
        videoRef.current.currentTime = time;
    };

    // --- Feature Event Handlers ---
    const handleToggleFullscreen = () => {
        if (!document.fullscreenElement) {
            playerContainerRef.current.requestFullscreen().catch(err => {
                alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const handleSelectSubtitle = (id) => {
        const video = videoRef.current;
        if (!video) return;

        const tracks = video.textTracks;
        // Find the specific subtitle from our prop array to get its details
        const selectedSub = subtitles.find(s => s.id === id);


        // First, hide all tracks to ensure a clean slate.
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = 'hidden';
        }

        // If a subtitle was selected (i.e., not 'Off'), find its corresponding track and show it.
        // We match by the `label` which should be unique.
        if (selectedSub) {
            for (let i = 0; i < tracks.length; i++) {
                if (tracks[i].label === selectedSub.label) {
                    tracks[i].mode = 'showing';
                    break; // Exit loop once found and enabled
                }
            }
        }

        // Update the state to reflect the change in the UI (e.g., highlighting the menu item).
        setActiveSubtitleId(id);
    };

    const handleSelectQuality = (id) => {
        const hls = hlsRef.current;
        if (hls) {
            if (id === 'auto') {
                hls.currentLevel = -1; // Enable Automatic Bitrate switching
            } else {
                hls.currentLevel = id; // id is the level index
            }
            setActiveQualityId(id);
        }
    };

    // --- Video Element Listeners ---
    useEffect(() => {
        const video = videoRef.current;
        const container = playerContainerRef.current;

        const handlePlaying = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleDurationChange = () => setDuration(video.duration);
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);

        video?.addEventListener('playing', handlePlaying);
        video?.addEventListener('pause', handlePause);
        video?.addEventListener('timeupdate', handleTimeUpdate);
        video?.addEventListener('durationchange', handleDurationChange);
        container?.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            video?.removeEventListener('playing', handlePlaying);
            video?.removeEventListener('pause', handlePause);
            video?.removeEventListener('timeupdate', handleTimeUpdate);
            video?.removeEventListener('durationchange', handleDurationChange);
            container?.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // --- Controls Visibility Logic ---
    useEffect(() => {
        const container = playerContainerRef.current;
        const video = videoRef.current;

        const showControls = () => {
            clearTimeout(hideControlsTimerRef.current);
            setAreControlsVisible(true);
        };

        const hideControls = () => {
            setAreControlsVisible(false);
        };

        const startHideTimer = () => {
            clearTimeout(hideControlsTimerRef.current);
            hideControlsTimerRef.current = setTimeout(() => {
                // Don't hide if the video is paused
                if (!video.paused) {
                    hideControls();
                }
            }, 3000); // Hide after 3 seconds
        };

        const handleMouseMove = () => {
            showControls();
            if (isFullscreen && isPlaying) {
                startHideTimer();
            }
        };

        const handleMouseLeave = () => {
            if (isFullscreen && isPlaying) {
                hideControls();
            }
        };

        // Show controls when paused, hide when playing (in fullscreen)
        if (!isPlaying) {
            showControls();
        } else {
            // If playing and in fullscreen, start the hide timer
            if (isFullscreen) {
                startHideTimer();
            }
        }

        // When exiting fullscreen, ensure controls are visible
        if (!isFullscreen) {
            showControls();
            clearTimeout(hideControlsTimerRef.current);
        }

        container?.addEventListener('mousemove', handleMouseMove);
        container?.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            container?.removeEventListener('mousemove', handleMouseMove);
            container?.removeEventListener('mouseleave', handleMouseLeave);
            clearTimeout(hideControlsTimerRef.current);
        };
    }, [isPlaying, isFullscreen]);



    return (
        <div
            ref={playerContainerRef}
            className={`relative w-full aspect-video bg-black group ${isFullscreen && !areControlsVisible ? 'cursor-none' : ''}`}
            onClick={handlePlayPauseToggle}
        >

            <video
                ref={videoRef}
                className={`w-full h-full ${areControlsVisible ? 'subtitles-visible' : ''}`}
                
                preload="metadata"
                crossOrigin="anonymous"
                autoPlay
                   webkit-playsinline="true"
                playsInline
                loop={false}
            >
                {subtitles.map(sub => (
                    <track
                        key={sub.id}
                        kind="subtitles"
                        src={url.replace('/index.m3u8', `/${sub.src}`)}
                        srcLang={sub.lang}
                        label={sub.label}
                        default={sub.default}
                        crossOrigin='anonymous'
                    />
                ))}
                Your browser does not support the video tag.
            </video>

            {playbackError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <p className="text-red-500">{playbackError}</p>
                </div>
            )}

            {!playbackError && (
                <VideoControls
                    isPlaying={isPlaying}
                    volume={volume}
                    isMuted={isMuted}
                    isFullscreen={isFullscreen}
                    areControlsVisible={areControlsVisible}
                    currentTime={currentTime}
                    duration={duration}
                    availableSubtitles={subtitles}
                    activeSubtitleId={activeSubtitleId}
                    availableQualities={availableQualities}
                    activeQualityId={activeQualityId}
                    onPlayPauseToggle={handlePlayPauseToggle}
                    onVolumeChange={handleVolumeChange}
                    onToggleMute={handleToggleMute}
                    onToggleFullscreen={handleToggleFullscreen}
                    onSeek={handleSeek}
                    onSelectSubtitle={handleSelectSubtitle}
                    onSelectQuality={handleSelectQuality}
                    fileName={fileName}
                />
            )}
        </div>
    );
};

export default VideoPlayer; 